import React, { createContext, useState, useEffect, useContext } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus} from '@azure/msal-browser';
import { getPostLoginRoute, normalizeStoredUserData, syncUserWithBackend } from './auth';
import getAccessToken from './authToken';
import { useToast } from "../components/ToastContext";
import {api}from './api';
import apiLogin from './apiLogin';
import { useNavigate } from 'react-router-dom';
import { applyAccountSelection, getPendingAccountSelection, getSharedAccountSelection } from './accountSelection';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const msalAuthenticated = useIsAuthenticated(); // MSAL's isAuthenticated
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const authState = sessionStorage.getItem('isAuthenticated');
    if (authState === 'true') {
      setIsAuthenticated(true);
    }
  }, []);


  useEffect(() => {
    const syncUser = async () => {
      if (inProgress !== InteractionStatus.None) return;

      let account = instance.getActiveAccount();
      if (!account && accounts.length > 0) {
        account = accounts[0];
        instance.setActiveAccount(account);
      }

      if (msalAuthenticated && account) {
        try {
          const result = await syncUserWithBackend(instance);
         
          if (result?.error) {
            toast.error("Login failed. Please contact support.");
            setIsAuthenticated(false);
            return;
          }

          const normalizedResult = normalizeStoredUserData(result);
          setUser(normalizedResult);
          setIsAuthenticated(true);
         
          if (normalizedResult.userid || normalizedResult.userId) {
              sessionStorage.setItem('userId', normalizedResult.userid || normalizedResult.userId);
          }
          if (normalizedResult.rolename) {
              sessionStorage.setItem('rolename', normalizedResult.rolename);
          }
          if (normalizedResult.organizationId) {
             sessionStorage.setItem('organizationId', normalizedResult.organizationId);
          }
       
          const sharedSelection = getSharedAccountSelection();
          if (
            sharedSelection?.userId === sessionStorage.getItem('userId') &&
            !getPendingAccountSelection()
          ) {
            // SSO synchronization must not erase an account accepted before reloading.
            applyAccountSelection(sharedSelection);
            sessionStorage.removeItem('justLoggedIn');
          } else {
            sessionStorage.removeItem('activeAccountId');
          }

          const justLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
         
          if (justLoggedIn) {
            navigate(getPostLoginRoute(normalizedResult.rolename));
           
          }

        } catch (error) {
          console.error("Backend sync failed:", error);
          toast.error("Login failed. Please try again.");
          setIsAuthenticated(false);
        }
      }
    };

    syncUser();
  }, [msalAuthenticated, accounts, instance, inProgress]);

  useEffect(() => {
    if (isAuthenticated) {
     sessionStorage.setItem('isAuthenticated', 'true');
    } else {
      sessionStorage.removeItem('isAuthenticated');
    }
  }, [isAuthenticated]);

  // Auto-refresh access token every 15 minutes
 useEffect(() => {
  const isSSOLogin = localStorage.getItem('ssoLogin') === 'true';

  const refreshToken = async () => {
    try {
      const token = await getAccessToken(instance);
      setAccessToken(token);
      sessionStorage.setItem('accessToken', token);
     
    } catch (error) {
      console.error("Access token refresh failed:", error);
      toast.error("Session expired. Please login again.", { autoClose: 3000 });
    }
  };

  if (isAuthenticated && isSSOLogin) {
    refreshToken(); // Initial fetch
    const interval = setInterval(refreshToken, 15 * 60 * 1000); // Every 15 minutes
    return () => clearInterval(interval);
  }
}, [isAuthenticated, instance]);


  const login = async ({ username, password }) => {
    // Your custom portal login logic
    try {
      const { userid, userRole, name, activeAccountId, gender, organizationId,organizationName } = await apiLogin({ username, password });
          // localStorage.setItem('clientid', clientId);
    sessionStorage.setItem('userId', userid);
   sessionStorage.setItem('rolename', userRole);
   sessionStorage.setItem('username', name);
    sessionStorage.setItem('activeAccountId', activeAccountId);
    localStorage.setItem('gender', gender);
      sessionStorage.setItem('organizationId', organizationId);
    sessionStorage.setItem('organizationName', organizationName);
      setIsAuthenticated(true);
     
      toast.success('Login successful');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Invalid username or password');
    }
  };

  const loginWithSSO = () => {
   
  sessionStorage.setItem('justLoggedIn', 'true');

    instance.loginRedirect({
      scopes: ['User.Read'],
    });
  };

  const logout = async () => {
    // Logout logic
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    let userId = sessionStorage.getItem('userId');

    if (userId) {
      try {
        const token = await getAccessToken(instance);
        const correlationId = crypto.randomUUID();

        await api.post(`/Account/logout/${userId}`, {}, {
          headers: {
            'X-CORRELATION-ID': correlationId,
            'Authorization': `Bearer ${token}`,
          },
        });

     
      } catch (error) {
        console.error(" Backend logout failed:", error);
        toast.error("Backend logout failed. Please try again.", { autoClose: 2000 });
      }
    }

    localStorage.clear();
     sessionStorage.clear();
    setIsAuthenticated(false);
    instance.logoutRedirect({
      postLogoutRedirectUri: '/login',
    });
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, loginWithSSO, logout, accessToken, setIsAuthenticated  }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
